import { ref, computed } from 'vue';
import type { ImageCapturePayload, ImageAnnotationContext } from '@/core/types/cameraCapture';
import type {
  DeviceImageLinkages,
  DeviceImageRecord,
  DeviceImageReferenceDto,
  DeviceImageSasUrlDto,
} from '@/domains/deviceImages/types/deviceImagesType';
import {
  DEVICES_IMAGES_CACHE_NAME,
  deleteDeviceImageLocalRecord,
  getDeviceImagesByStopId,
  saveSuccessfulUploadToCache,
  upsertDeviceImageReferences,
} from '@/domains/deviceImages/repositories/deviceImageRepository';
import { saveImageForSync } from '@/domains/deviceImages/services/deviceImagesService';
import { apiRequest, useApiMutation } from '@/composables/useApi';
import { useNotificationStore } from '@/core/notifications/useNotificationStore';
import { setLoaderConfig, startRequest, endRequest } from '@/core/loading/globalLoadingStore';
import { deviceImageEndpoints } from '@/services/api/apiConfig';
import { syncService, SyncJobTypes } from '@/services/sync/syncService';
import { db } from '@/db/dexieClient';
import { useConstants } from '@/domains/constants';
import { useDeviceImageStore } from '@/domains/deviceImages/stores/useDeviceImageStore';
import { MAX_POD_PHOTOS_PER_STOP } from '@/constants/deviceImages';
import type { ApiResponse } from '@/types/api';

interface UploadImageVariables {
  deviceImageTypeId: number;
  entityId: number;
  formData: FormData;
}
interface UploadImageResponse {
  id: number;
  fileName: string;
  contentType: string;
  size: number;
}

const error = ref<Error | null>(null);

export const useDeviceImages = () => {
  // === COMPOSABLE STRUCTURE ===

  // Dependencies
  const imagesStore = useDeviceImageStore();
  const notificationStore = useNotificationStore();
  const { actions: constantsActions } = useConstants();

  // Infrastructure helpers
  let constantsInitialized = false;

  const ensureConstantsInitialized = async () => {
    if (constantsInitialized) return;
    await constantsActions.initialize();
    constantsInitialized = true;
  };

  const getPodImageTypeId = async (): Promise<number | null> => {
    try {
      await ensureConstantsInitialized();
      return constantsActions.getDeviceImageTypeByCode('PD').id;
    } catch {
      return null;
    }
  };

  const resolveDeviceImageAssociations = async (
    deviceImageTypeId: number,
    entityId: number
  ): Promise<DeviceImageLinkages> => {
    try {
      await ensureConstantsInitialized();
      const deviceImageType = constantsActions.getDeviceImageTypeById(deviceImageTypeId);
      const code = deviceImageType.code?.toUpperCase?.();

      switch (code) {
        case 'CR':
          return {
            routeId: entityId,
            routeStopId: null,
            shipmentGuid: null,
          };
        case 'PD':
        case 'DS': {
          const routeStop = await db.routeStops.get(entityId);
          return {
            routeId: routeStop?.routeId ?? null,
            routeStopId: entityId,
            shipmentGuid: null,
          };
        }
        case 'SA': {
          const shipment = await db.shipments.get(entityId);
          return {
            routeId: shipment?.routeId ?? null,
            routeStopId: null,
            shipmentGuid: shipment?.shipmentGuid ?? null,
          };
        }
        default:
          return {};
      }
    } catch (associationError) {
      console.warn(
        '[useDeviceImages] Failed to resolve associations for device image type',
        deviceImageTypeId,
        associationError
      );
      return {};
    }
  };

  const uploadMutation = useApiMutation<UploadImageResponse, Error, UploadImageVariables>({
    endpoint: deviceImageEndpoints.UPLOAD(0, 0),
    method: 'post',
    mapVariablesToRequest: ({ deviceImageTypeId, entityId, formData }) => ({
      endpoint: deviceImageEndpoints.UPLOAD(deviceImageTypeId, entityId),
      body: formData,
    }),
  });

  // === BUSINESS LOGIC ===

  // Business Actions
  const addCapturedImageWrapper = async (params: {
    entityId: number;
    deviceImageTypeId: number;
    payload: ImageCapturePayload;
  }) => {
    const { entityId, deviceImageTypeId, payload } = params;

    if (uploadMutation.isPending.value) return;
    error.value = null;

    const podImageTypeId = await getPodImageTypeId();
    if (podImageTypeId !== null && deviceImageTypeId === podImageTypeId) {
      const podCount = await getPodImageCountByStopId(entityId);
      if (podCount >= MAX_POD_PHOTOS_PER_STOP) {
        const limitError = new Error(
          `Maximum POD photo limit (${MAX_POD_PHOTOS_PER_STOP}) reached for this stop.`
        );
        error.value = limitError;
        notificationStore.warning(limitError.message, { duration: 5000 });
        return { success: false, strategy: 'failed' as const, error: limitError };
      }
    }

    setLoaderConfig({ variant: 'card', message: 'Saving image...', backdrop: true });
    startRequest();

    const associations: DeviceImageLinkages = await resolveDeviceImageAssociations(
      deviceImageTypeId,
      entityId
    );

    try {
      const formData = new FormData();
      const filename = `capture_${entityId}_${Date.now()}.jpg`;
      formData.append('image', payload.blob, filename);
      if (payload.annotationMetadata) {
        formData.append('capturedAtLocalIso', payload.annotationMetadata.capturedAtLocalIso);
        formData.append('localTimezone', payload.annotationMetadata.localTimezone);
      }

      if (!navigator.onLine) {
        throw new Error('Browser is offline. Failing fast.');
      }

      const result = await uploadMutation.mutateAsync({
        deviceImageTypeId,
        entityId,
        formData,
      });

      const cacheRecord = await saveSuccessfulUploadToCache(
        result,
        {
          entityId,
          deviceImageTypeId,
          timestamp: Date.now(),
          latitude: payload.location?.latitude,
          longitude: payload.location?.longitude,
          ...associations,
        },
        payload.blob
      );

      notificationStore.success('Image uploaded successfully.', { duration: 3000 });
      return { success: true, strategy: 'online' as const, data: result, cacheRecord };
    } catch (onlineError) {
      console.warn(
        '[useDeviceImages] Online upload failed, starting offline fallback.',
        onlineError
      );

      try {
        const fallbackResult = await saveImageForSync(
          entityId,
          deviceImageTypeId,
          payload.blob,
          payload.location,
          associations
        );

        imagesStore.addToOfflineQueue(fallbackResult);
        notificationStore.info(
          'Image was not uploaded, will be saved later. You can continue working.',
          {
            duration: 5000,
          }
        );

        return { success: true, strategy: 'offline' as const, data: fallbackResult };
      } catch (offlineError) {
        console.error('Fatal: Offline fallback failed.', offlineError);
        error.value = offlineError as Error;
        notificationStore.info(
          'Image was not uploaded, will be saved later. You can continue working.',
          {
            duration: 5000,
          }
        );
        return { success: false, strategy: 'failed' as const, error: offlineError };
      }
    } finally {
      endRequest();
    }
  };

  const clearOfflineQueue = () => {
    imagesStore.clearOfflineQueue();
  };

  const loadDeviceImageReferencesByRouteId = async (routeId: number): Promise<void> => {
    if (!routeId || routeId <= 0) {
      return;
    }

    try {
      const response = await apiRequest<ApiResponse<DeviceImageReferenceDto[]>>({
        endpoint: deviceImageEndpoints.LIST_BY_ROUTE(routeId),
        method: 'get',
      });

      const references = Array.isArray(response.data) ? response.data : [];
      await upsertDeviceImageReferences(references);
    } catch (loadError) {
      console.warn('[useDeviceImages] Failed to load device image references by route id:', {
        routeId,
        error: loadError instanceof Error ? loadError.message : String(loadError),
      });
    }
  };

  const loadDeviceImageReferencesByRouteStopId = async (routeStopId: number): Promise<void> => {
    if (!routeStopId || routeStopId <= 0) {
      return;
    }

    try {
      const response = await apiRequest<ApiResponse<DeviceImageReferenceDto[]>>({
        endpoint: deviceImageEndpoints.LIST_BY_ROUTE_STOP(routeStopId),
        method: 'get',
      });

      const references = Array.isArray(response.data) ? response.data : [];
      await upsertDeviceImageReferences(references);
    } catch (loadError) {
      console.warn('[useDeviceImages] Failed to load device image references by route stop id:', {
        routeStopId,
        error: loadError instanceof Error ? loadError.message : String(loadError),
      });
    }
  };

  const prefetchDeviceImageBlob = async (cache: Cache, image: DeviceImageRecord): Promise<void> => {
    if (!image.id || image.id <= 0 || image.syncStatus !== 'synced' || !image.cacheKey) {
      return;
    }

    if (await cache.match(image.cacheKey)) {
      return;
    }

    try {
      const sasResponse = await apiRequest<ApiResponse<DeviceImageSasUrlDto>>({
        endpoint: deviceImageEndpoints.GET_SAS_URL(image.id),
        method: 'get',
      });

      const sasUrl = sasResponse.data?.url;
      if (!sasUrl) {
        return;
      }

      const imageResponse = await fetch(sasUrl);
      if (!imageResponse.ok) {
        return;
      }

      const imageBlob = await imageResponse.blob();
      if (!imageBlob.size) {
        return;
      }

      await cache.put(
        image.cacheKey,
        new Response(imageBlob, {
          headers: { 'Content-Type': imageBlob.type || 'image/jpeg' },
        })
      );
    } catch {
      // Best-effort prefetch only. Keep this silent to avoid interrupting scan entry.
    }
  };

  const preloadStopDeviceImages = async (routeStopId: number): Promise<void> => {
    await loadDeviceImageReferencesByRouteStopId(routeStopId);

    if (!navigator.onLine) {
      return;
    }

    const images = await getDeviceImagesByStopId(routeStopId);
    if (images.length === 0) {
      return;
    }

    const cache = await caches.open(DEVICES_IMAGES_CACHE_NAME);
    await Promise.all(images.map((image) => prefetchDeviceImageBlob(cache, image)));
  };

  // Business Selectors
  const getDeviceImagesByStopIdFromCache = async (
    routeStopId: number
  ): Promise<DeviceImageRecord[]> => {
    return getDeviceImagesByStopId(routeStopId);
  };

  const getPodImageCountByStopId = async (routeStopId: number): Promise<number> => {
    const podImageTypeId = await getPodImageTypeId();
    if (podImageTypeId === null) return 0;

    const images = await getDeviceImagesByStopId(routeStopId);
    return images.filter((image) => image.deviceImageTypeId === podImageTypeId).length;
  };

  const getPodImagesByStopId = async (routeStopId: number): Promise<DeviceImageRecord[]> => {
    const podImageTypeId = await getPodImageTypeId();
    if (podImageTypeId === null) {
      return [];
    }

    const images = await getDeviceImagesByStopId(routeStopId);
    return images
      .filter((image) => image.deviceImageTypeId === podImageTypeId)
      .sort((a, b) => b.timestamp - a.timestamp);
  };

  const deleteDeviceImage = async (image: DeviceImageRecord): Promise<void> => {
    const imageId = image.id;
    if (imageId == null) {
      return;
    }

    const isLocalOnly = image.syncStatus !== 'synced' || imageId <= 0;

    if (isLocalOnly) {
      await deleteDeviceImageLocalRecord(image);
      imagesStore.removeFromOfflineQueue(imageId);
      return;
    }

    if (!navigator.onLine) {
      await syncService.enqueue({
        type: SyncJobTypes.IMAGE_DELETE,
        newRecord: { imageId },
        oldRecord: null,
      });

      await deleteDeviceImageLocalRecord(image);
      imagesStore.removeFromOfflineQueue(imageId);
      return;
    }

    await apiRequest({
      endpoint: deviceImageEndpoints.DELETE(imageId),
      method: 'delete',
    });

    await deleteDeviceImageLocalRecord(image);
    imagesStore.removeFromOfflineQueue(imageId);
  };

  const hasDeviceImagesByStopId = async (routeStopId: number): Promise<boolean> => {
    const images = await getDeviceImagesByStopId(routeStopId);
    return images.length > 0;
  };

  const getImageAnnotationContext = async (
    deviceImageTypeId: number,
    entityId: number
  ): Promise<ImageAnnotationContext | null> => {
    try {
      await ensureConstantsInitialized();
      const deviceImageType = constantsActions.getDeviceImageTypeById(deviceImageTypeId);
      const code = deviceImageType.code?.toUpperCase?.();

      if (code !== 'PD' && code !== 'DS') {
        return null;
      }

      const routeStop = await db.routeStops.get(entityId);
      if (!routeStop?.routeId) {
        return {
          topLine: null,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
      }

      const [route, address] = await Promise.all([
        db.routes.get(routeStop.routeId),
        db.addresses.get(routeStop.addressId),
      ]);

      const routeCode = route?.code?.trim() || null;
      const stopCode =
        address?.codeAlternate?.trim() ||
        (routeStop.stopNumber ? String(routeStop.stopNumber) : null) ||
        address?.name?.trim() ||
        null;

      const topLine = routeCode && stopCode ? `Route: ${routeCode} Stop: ${stopCode}` : null;

      return {
        topLine,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
    } catch (annotationError) {
      console.warn('[useDeviceImages] Failed to resolve annotation context', annotationError);
      return {
        topLine: null,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
    }
  };

  // === COMPOSABLE OUTPUT ===

  const offlineQueue = computed(() => imagesStore.offlineQueue);

  const state = {
    offlineQueue,
  };

  const selectors = {
    getDeviceImagesByStopIdFromCache,
    getPodImageCountByStopId,
    getPodImagesByStopId,
    hasDeviceImagesByStopId,
    getImageAnnotationContext,
  };

  const actions = {
    addCapturedImage: addCapturedImageWrapper,
    clearOfflineQueue,
    deleteDeviceImage,
    loadDeviceImageReferencesByRouteId,
    loadDeviceImageReferencesByRouteStopId,
    preloadStopDeviceImages,
  };

  const status = {
    isLoading: computed(() => uploadMutation.isPending.value),
    error: computed(() => error.value || uploadMutation.error.value),
  };

  return {
    state,
    selectors,
    actions,
    status,
  };
};
